export const buildCoachSchedulingFlagsMap = (coachesList = []) =>
  coachesList.reduce((acc, coach) => {
    const coachId = coach?._id || coach?.id
    if (!coachId) {
      return acc
    }

    acc[coachId] = {
      isScheduleEnabled: coach?.isScheduleEnabled,
      hasVideoCallLink: coach?.hasVideoCallLink,
      hasCoachBio: coach?.hasCoachBio,
      hasQuestBio: coach?.hasQuestBio,
    }
    return acc
  }, {})

export const resolveCoachSchedulingFlags = (
  coachId,
  {
    coachFromClient,
    coachFlagsMap = {},
    coachSchedulingFlagsFromState = {},
    activeCoachId,
  } = {},
) => ({
  isScheduleEnabled:
    coachFromClient?.isScheduleEnabled ??
    coachFlagsMap?.[coachId]?.isScheduleEnabled ??
    (coachId === activeCoachId
      ? coachSchedulingFlagsFromState?.isScheduleEnabled
      : undefined),
  hasVideoCallLink:
    coachFromClient?.hasVideoCallLink ??
    coachFlagsMap?.[coachId]?.hasVideoCallLink ??
    (coachId === activeCoachId
      ? coachSchedulingFlagsFromState?.hasVideoCallLink
      : undefined),
  hasCoachBio:
    coachFromClient?.hasCoachBio ??
    coachFlagsMap?.[coachId]?.hasCoachBio ??
    (coachId === activeCoachId
      ? coachSchedulingFlagsFromState?.hasCoachBio
      : undefined),
  hasQuestBio:
    coachFromClient?.hasQuestBio ??
    coachFlagsMap?.[coachId]?.hasQuestBio ??
    (coachId === activeCoachId
      ? coachSchedulingFlagsFromState?.hasQuestBio
      : undefined),
})

export const isMissingAllBios = ({ hasCoachBio, hasQuestBio } = {}) =>
  hasCoachBio === false && hasQuestBio === false

export const isPlatformSchedulingAvailable = ({
  hasVideoCallLink,
  isScheduleEnabled,
  hasCoachBio,
  hasQuestBio,
} = {}) => {
  return (
    isScheduleEnabled !== false &&
    hasVideoCallLink !== false &&
    !isMissingAllBios({ hasCoachBio, hasQuestBio })
  )
}

export const getPlatformSchedulingDisabledMessage = ({
  hasVideoCallLink,
  isScheduleEnabled,
  hasCoachBio,
  hasQuestBio,
} = {}) => {
  const missing = []
  if (isScheduleEnabled === false) missing.push('connected their calendar')
  if (hasVideoCallLink === false) missing.push('provided a video call link')
  if (isMissingAllBios({ hasCoachBio, hasQuestBio })) {
    missing.push('set up their executive coaching bio or quest bio')
  }

  if (missing.length === 0) {
    return ''
  }
  if (missing.length === 1) {
    return `The coach has not ${missing[0]} yet.`
  }
  const last = missing.pop()
  return `The coach has not ${missing.join(', ')} and has not ${last}.`
}

export const getCoachSchedulingIssue = (coach) => {
  const schedulingFlags = {
    isScheduleEnabled: coach?.isScheduleEnabled,
    hasVideoCallLink: coach?.hasVideoCallLink,
    hasCoachBio: coach?.hasCoachBio,
    hasQuestBio: coach?.hasQuestBio,
  }

  if (isPlatformSchedulingAvailable(schedulingFlags)) {
    return null
  }

  return {
    coachId: coach?._id || coach?.value || coach?.id,
    coachName: coach?.fullName || coach?.label,
    message: getPlatformSchedulingDisabledMessage(schedulingFlags),
  }
}

export const getUniqueCoachSchedulingIssues = (
  assignments = [],
  coaches = [],
  selectedUsers = [],
) => {
  const userMap = selectedUsers.reduce((acc, user) => {
    acc[user._id] =
      user.fullName || `${user.fname || ''} ${user.lname || ''}`.trim()
    return acc
  }, {})
  const coachMap = coaches.reduce((acc, coach) => {
    acc[coach.value] = coach
    return acc
  }, {})
  const issuesByCoachId = new Map()

  assignments.forEach(({ userId, coachId }) => {
    if (!coachId) {
      return
    }

    const coach = coachMap[coachId]
    const issue = getCoachSchedulingIssue(coach)

    if (!issue) {
      return
    }

    if (!issuesByCoachId.has(coachId)) {
      issuesByCoachId.set(coachId, {
        ...issue,
        clients: [],
      })
    }

    const clientName = userMap[userId]
    if (
      clientName &&
      !issuesByCoachId.get(coachId).clients.includes(clientName)
    ) {
      issuesByCoachId.get(coachId).clients.push(clientName)
    }
  })

  return Array.from(issuesByCoachId.values())
}

export const buildCoachSchedulingFlagsMapFromOptions = (coaches = []) =>
  coaches.reduce((acc, coach) => {
    if (!coach?.value) {
      return acc
    }

    acc[coach.value] = {
      isScheduleEnabled: coach?.isScheduleEnabled,
      hasVideoCallLink: coach?.hasVideoCallLink,
      hasCoachBio: coach?.hasCoachBio,
      hasQuestBio: coach?.hasQuestBio,
    }
    return acc
  }, {})

export const canBulkAssignUsePlatformScheduling = (
  coachIds = [],
  coachSchedulingFlagsByCoachId = {},
) =>
  coachIds.every((coachId) =>
    isPlatformSchedulingAvailable(coachSchedulingFlagsByCoachId?.[coachId]),
  )

export const getBulkPlatformSchedulingDisabledMessage = (
  coachIds = [],
  coaches = [],
  coachSchedulingFlagsByCoachId = {},
) => {
  const messages = coachIds
    .map((coachId) => {
      const flags = coachSchedulingFlagsByCoachId?.[coachId] || {}
      if (isPlatformSchedulingAvailable(flags)) {
        return null
      }

      const coach = coaches.find(
        (coachOption) => coachOption?.value === coachId,
      )
      const coachName = coach?.fullName || coach?.label
      const message = getPlatformSchedulingDisabledMessage(flags)

      return coachName ? `${coachName}: ${message}` : message
    })
    .filter(Boolean)

  if (messages.length === 0) {
    return ''
  }

  if (messages.length === 1) {
    return messages[0].includes(': ')
      ? messages[0].split(': ').slice(1).join(': ')
      : messages[0]
  }

  return 'One or more selected coaches are not set up for platform scheduling.'
}

const getRequiredBioFlagKey = (sowType) =>
  sowType === 'QUEST' ? 'hasQuestBio' : 'hasCoachBio'

export const isBioInMailAvailable = (flags = {}, sowType) => {
  if (isMissingAllBios(flags || {})) {
    return false
  }
  return flags?.[getRequiredBioFlagKey(sowType)] !== false
}

export const getBioInMailDisabledMessage = (flags = {}, sowType) => {
  if (isBioInMailAvailable(flags, sowType)) {
    return ''
  }
  if (isMissingAllBios(flags || {})) {
    return 'The coach has not set up their executive coaching bio or quest bio.'
  }
  return sowType === 'QUEST'
    ? 'The coach has not set up their quest bio yet.'
    : 'The coach has not set up their executive coaching bio yet.'
}

const getBulkBioLabel = (sowType) =>
  sowType === 'QUEST' ? 'quest bio' : 'executive coaching bio'

const getBulkCoachesWithoutBio = (
  coachIds = [],
  coachSchedulingFlagsByCoachId = {},
  sowType,
) =>
  coachIds.filter(
    (coachId) =>
      !isBioInMailAvailable(coachSchedulingFlagsByCoachId?.[coachId], sowType),
  )

const getCoachNames = (coachIds = [], coaches = []) =>
  coachIds
    .map((coachId) => {
      const coach = coaches.find(
        (coachOption) => coachOption?.value === coachId,
      )
      return coach?.fullName || coach?.label
    })
    .filter(Boolean)

// Bulk assign: the bio can be included as long as at least one paired coach has the required bio.
// Each client's email follows that client's own coach.
export const canBulkAssignIncludeBioInMail = (
  coachIds = [],
  coachSchedulingFlagsByCoachId = {},
  sowType,
) =>
  coachIds.length > 0 &&
  getBulkCoachesWithoutBio(coachIds, coachSchedulingFlagsByCoachId, sowType)
    .length < coachIds.length

// Shown when no paired coach has the required bio (checkbox disabled)
export const getBulkBioInMailDisabledMessage = (
  coachIds = [],
  coaches = [],
  coachSchedulingFlagsByCoachId = {},
  sowType,
) => {
  if (
    canBulkAssignIncludeBioInMail(
      coachIds,
      coachSchedulingFlagsByCoachId,
      sowType,
    )
  ) {
    return ''
  }
  return `None of the selected coaches have set up their ${getBulkBioLabel(
    sowType,
  )}, so a bio cannot be included in the schedule email.`
}

// Shown next to an enabled checkbox when only some paired coaches lack the required bio
export const getBulkBioInMailPartialWarning = (
  coachIds = [],
  coaches = [],
  coachSchedulingFlagsByCoachId = {},
  sowType,
) => {
  const missingCoachIds = getBulkCoachesWithoutBio(
    coachIds,
    coachSchedulingFlagsByCoachId,
    sowType,
  )
  if (
    missingCoachIds.length === 0 ||
    missingCoachIds.length === coachIds.length
  ) {
    return ''
  }

  const coachNames = getCoachNames(missingCoachIds, coaches)
  const who =
    coachNames.length > 0
      ? `${coachNames.join(', ')}'s clients`
      : "some coaches' clients"
  return `The bio will not be included for ${who} because ${
    missingCoachIds.length === 1 ? 'the coach has' : 'those coaches have'
  } not set up the ${getBulkBioLabel(sowType)}.`
}

export const runSchedulingPreconditionChecks = ({
  hasVideoCallLink,
  isScheduleEnabled,
  onRequireVideoCallLink,
  onRequireAvailability,
}) => {
  if (isScheduleEnabled === false) {
    if (onRequireAvailability) {
      onRequireAvailability()
    }
    return false
  }
  if (hasVideoCallLink === false) {
    if (onRequireVideoCallLink) {
      onRequireVideoCallLink()
    }
    return false
  }
  return true
}
